import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import authService from '../../api/authService';

const ChangePassword = ({ email, verifiedOtp, onPasswordChanged, onBack, getSessionConflictOptions }) => {
  const [formData, setFormData] = useState({
    currentPassword: '', // Default password set during registration by admin/super-admin
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('One number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('One special character');
    }
    
    return errors;
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordErrors = validatePassword(formData.newPassword);

    // Validate current password (default password from registration)
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    // Note: Current password validation will be done by the backend API

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordErrors.length > 0) {
      newErrors.newPassword = `Password must contain: ${passwordErrors.join(', ')}`;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const sessionConflictOptions =
        typeof getSessionConflictOptions === 'function' ? getSessionConflictOptions() : {};
      const response = await authService.changePasswordFirstTime(
        email,
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword,
        sessionConflictOptions
      );

      if (response.token) {
        // Password changed successfully, token is stored in authService
        localStorage.setItem(`password_set_${email}`, 'true');
        toast.success('Password changed successfully!');
        // Call success callback
        onPasswordChanged();
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      if (
        error?.message === 'LOGIN_POLL_ABORTED' ||
        error?.message === 'SESSION_FLOW_TERMINATED' ||
        error?.message === 'LOGIN_REJECTED' ||
        error?.name === 'AbortError'
      ) {
        return;
      }
      console.error('Password change error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to change password. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordErrors = formData.newPassword ? validatePassword(formData.newPassword) : [];
  const passwordStrength = formData.newPassword 
    ? Math.max(0, 5 - passwordErrors.length) 
    : 0;

  return (
    <div className="w-full max-w-md">
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl font-bold" style={{ color: '#333333' }}>
            Set Your Password
          </CardTitle>
          <CardDescription className="text-sm" style={{ color: '#707070' }}>
            This is your first login. Please enter the default password set during registration and create a new secure password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password (Default password from registration) */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-sm font-medium" style={{ color: '#333333' }}>
                Current Password
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => handleChange('currentPassword', e.target.value)}
                  className="w-full pr-12"
                  style={{
                    borderColor: errors.currentPassword ? '#ef4444' : '#DADADA',
                    background: '#F5F5F5'
                  }}
                  placeholder="Enter default password from registration"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4"
                  style={{ color: '#707070' }}
                >
                  {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs" style={{ color: '#707070' }}>
                Enter the default password that was set when your account was created by the administrator
              </p>
              {errors.currentPassword && (
                <p className="text-sm text-red-600">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-medium" style={{ color: '#333333' }}>
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleChange('newPassword', e.target.value)}
                  className="w-full pr-12"
                  style={{
                    borderColor: errors.newPassword ? '#ef4444' : '#DADADA',
                    background: '#F5F5F5'
                  }}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4"
                  style={{ color: '#707070' }}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className="h-1 flex-1 rounded"
                        style={{
                          background: level <= passwordStrength ? '#307A91' : '#E5E5E5'
                        }}
                      />
                    ))}
                  </div>
                  {passwordErrors.length > 0 && (
                    <ul className="text-xs space-y-1" style={{ color: '#ef4444' }}>
                      {passwordErrors.map((error, index) => (
                        <li key={index} className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {error}
                        </li>
                      ))}
                    </ul>
                  )}
                  {passwordErrors.length === 0 && formData.newPassword && (
                    <p className="text-xs flex items-center gap-1" style={{ color: '#10b981' }}>
                      <CheckCircle className="w-3 h-3" />
                      Password meets all requirements
                    </p>
                  )}
                </div>
              )}
              
              {errors.newPassword && (
                <p className="text-sm text-red-600">{errors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium" style={{ color: '#333333' }}>
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className="w-full pr-12"
                  style={{
                    borderColor: errors.confirmPassword ? '#ef4444' : '#DADADA',
                    background: '#F5F5F5'
                  }}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4"
                  style={{ color: '#707070' }}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {errors.submit}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 font-bold text-white"
              style={{
                background: '#307A91',
                opacity: loading ? 0.5 : 1
              }}
            >
              {loading ? 'Setting Password...' : 'Set Password & Continue'}
            </Button>

            {/* Back Button */}
            <button
              type="button"
              onClick={onBack}
              className="w-full text-sm text-center transition-colors"
              style={{ color: '#707070' }}
              onMouseEnter={(e) => e.target.style.color = '#307A91'}
              onMouseLeave={(e) => e.target.style.color = '#707070'}
            >
              ← Back to OTP verification
            </button>
            
            {/* Development Helper - Show Default Password */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-center" style={{ color: '#707070' }}>
                <button
                  type="button"
                  onClick={() => {
                    const defaultPassword = localStorage.getItem(`default_password_${email}`) || 'DefaultPass123!';
                    setFormData(prev => ({ ...prev, currentPassword: defaultPassword }));
                    toast.success('Default password filled automatically (dev mode)');
                  }}
                  className="underline hover:text-blue-600 transition-colors"
                >
                  Fill default password automatically (dev only)
                </button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;

